// Gast-Link: Wer eingeladen wurde, spricht im Browser mit – ohne Konto, ohne Installation.
// Mikrofon und Kopfhörer laufen über ein Audio-Worklet, der Ton über das Relay der Suite.

import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { workletSource } from "../guest/link-worklet";
import { AuthRahmen } from "../components/Logo";
import { LiveDot } from "../components/ui";

interface Invite {
  channel: string;
  host_name: string;
  expires_at: string;
}

interface Joined {
  token: string;
  channel: string;
  host_name: string;
}

type Phase = "loading" | "invalid" | "ready" | "joining" | "live" | "ended";

const FINAL_CLOSE: Record<number, string> = {
  4001: "Das Studio hat die Sendung beendet. Danke fürs Mitmachen!",
  4002: "Du bist in einem anderen Fenster verbunden.",
  4003: "Das Studio hat gerade keinen Platz frei.",
  4004: "Das Studio hat die Verbindung beendet.",
  4005: "Dieser Zugang gilt nicht mehr.",
  1008: "Verbindung abgelehnt.",
};

function Meter({ level, label }: { level: number; label: string }): JSX.Element {
  const pct = Math.min(100, Math.round(Math.sqrt(level) * 100));
  return (
    <div className="space-y-1" role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
      <div className="flex justify-between text-xs text-muted">
        <span>{label}</span>
        <span>{pct > 92 ? "zu laut" : ""}</span>
      </div>
      <div className="h-2.5 overflow-hidden rounded-full bg-input ring-1 ring-inset ring-[color:var(--border)]">
        <div className={`h-full rounded-full transition-[width] duration-75 ${pct > 92 ? "bg-red-500" : pct > 70 ? "bg-amber-400" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function GuestPage(): JSX.Element {
  const { token = "" } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>("loading");
  const [invite, setInvite] = useState<Invite | null>(null);
  const [name, setName] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [hostConnected, setHostConnected] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [meter, setMeter] = useState({ mic: 0, ret: 0, bufferMs: 0, loss: 0 });
  const [rtt, setRtt] = useState<number | null>(null);
  const [reconnecting, setReconnecting] = useState(false);

  const ctxRef = useRef<AudioContext | null>(null);
  const nodeRef = useRef<AudioWorkletNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const joinedRef = useRef<Joined | null>(null);
  const stoppedRef = useRef(false);

  useEffect(() => {
    fetch(`/api/comod/guest/${encodeURIComponent(token)}`)
      .then(async (res) => {
        const body = (await res.json()) as Invite & { message?: string };
        if (!res.ok) {
          setMessage(body.message ?? "Dieser Link gilt nicht (mehr).");
          setPhase("invalid");
          return;
        }
        setInvite(body);
        setPhase("ready");
      })
      .catch(() => {
        setMessage("Die Suite ist gerade nicht erreichbar. Bitte gleich nochmal versuchen.");
        setPhase("invalid");
      });
  }, [token]);

  const teardown = useCallback(() => {
    stoppedRef.current = true;
    wsRef.current?.close();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    void ctxRef.current?.close();
    wsRef.current = null;
    nodeRef.current = null;
    ctxRef.current = null;
  }, []);

  useEffect(() => teardown, [teardown]);

  const connect = useCallback((attempt = 0) => {
    const joined = joinedRef.current;
    const node = nodeRef.current;
    if (!joined || !node || stoppedRef.current) return;
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${proto}://${window.location.host}/api/relay/ws?role=participant&token=${encodeURIComponent(joined.token)}`);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;
    let seq = 0;
    let ping: number | undefined;

    node.port.onmessage = (e: MessageEvent<{ type: string; payload?: ArrayBuffer; mic?: number; ret?: number; bufferMs?: number; loss?: number }>) => {
      const m = e.data;
      if (m.type === "enc" && m.payload && ws.readyState === WebSocket.OPEN) {
        // Staut sich der Sendepuffer (schwaches Netz), lieber Rahmen auslassen als Verzögerung aufbauen.
        if (ws.bufferedAmount > 32_000) return;
        const frame = new Uint8Array(8 + m.payload.byteLength);
        frame[0] = 1;
        frame[3] = 1;
        new DataView(frame.buffer).setUint32(4, seq++, true);
        frame.set(new Uint8Array(m.payload), 8);
        ws.send(frame);
      } else if (m.type === "meter") {
        setMeter({ mic: m.mic ?? 0, ret: m.ret ?? 0, bufferMs: m.bufferMs ?? 0, loss: m.loss ?? 0 });
      }
    };

    ws.onopen = () => {
      setReconnecting(false);
      setPhase("live");
      ping = window.setInterval(() => ws.readyState === WebSocket.OPEN && ws.send(JSON.stringify({ type: "ping", t: performance.now() })), 2000);
    };
    ws.onmessage = (e: MessageEvent<ArrayBuffer | string>) => {
      if (typeof e.data === "string") {
        const msg = JSON.parse(e.data) as { type: string; host_connected?: boolean; connected?: boolean; t?: number };
        if (msg.type === "welcome") setHostConnected(!!msg.host_connected);
        if (msg.type === "host") setHostConnected(!!msg.connected);
        if (msg.type === "pong" && typeof msg.t === "number") setRtt(Math.round(performance.now() - msg.t));
        return;
      }
      const data = e.data;
      if (data.byteLength !== 332) return;
      const view = new DataView(data);
      node.port.postMessage({ type: "frame", seq: view.getUint32(4, true), payload: data.slice(8) }, []);
    };
    ws.onclose = (e) => {
      window.clearInterval(ping);
      if (stoppedRef.current) return;
      const final = FINAL_CLOSE[e.code];
      if (final) {
        setMessage(final);
        setPhase("ended");
        teardown();
        return;
      }
      setReconnecting(true);
      window.setTimeout(() => connect(attempt + 1), Math.min(10_000, 1000 * 2 ** Math.min(attempt, 3)));
    };
  }, [teardown]);

  const join = async (): Promise<void> => {
    setPhase("joining");
    setMessage(null);
    try {
      // AudioContext erst hier, im Tipp: iPhone und iPad erlauben Ton nur nach einer Berührung.
      const ctx = new AudioContext({ latencyHint: "interactive" });
      await ctx.resume();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 } });
      const url = URL.createObjectURL(new Blob([workletSource()], { type: "text/javascript" }));
      await ctx.audioWorklet.addModule(url);
      URL.revokeObjectURL(url);
      const node = new AudioWorkletNode(ctx, "wr-link", { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2] });
      ctx.createMediaStreamSource(stream).connect(node);
      node.connect(ctx.destination);

      const res = await fetch(`/api/comod/guest/${encodeURIComponent(token)}/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim() }),
      });
      const body = (await res.json()) as Joined & { message?: string };
      if (!res.ok) {
        stream.getTracks().forEach((t) => t.stop());
        void ctx.close();
        setMessage(body.message ?? "Beitreten hat nicht geklappt.");
        setPhase(res.status === 404 ? "invalid" : "ready");
        return;
      }
      ctxRef.current = ctx;
      streamRef.current = stream;
      nodeRef.current = node;
      joinedRef.current = body;
      stoppedRef.current = false;
      connect();
    } catch (err) {
      const denied = err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "SecurityError");
      setMessage(denied ? "Ohne Mikrofon geht es nicht. Bitte im Browser das Mikrofon erlauben und nochmal tippen." : "Mikrofon oder Ton ließ sich nicht starten. Anderer Browser? (Chrome, Safari, Firefox, Edge)");
      setPhase("ready");
    }
  };

  const toggleMute = (): void => {
    const next = !muted;
    setMuted(next);
    nodeRef.current?.port.postMessage({ type: "mute", on: next });
  };

  const changeVolume = (v: number): void => {
    setVolume(v);
    nodeRef.current?.port.postMessage({ type: "gain", value: v });
  };

  const leave = (): void => {
    teardown();
    setMessage("Du hast die Verbindung beendet. Danke!");
    setPhase("ended");
  };

  const until = invite ? new Date(invite.expires_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "";
  const channel = joinedRef.current?.channel ?? invite?.channel ?? "";
  const host = joinedRef.current?.host_name ?? invite?.host_name ?? "";

  return (
    <AuthRahmen
      breit
      titel="Live im Studio – direkt aus dem Browser."
      text="Ohne Konto, ohne Installation: Kopfhörer auf, Mikrofon erlauben und mitreden."
      punkte={["Kopfhörer aufsetzen", "Mikrofon erlauben", "Mit dem Studio sprechen"]}
    >
      <main className="card space-y-5 p-6 text-primary shadow-lg sm:p-8">
        <header className="space-y-1.5">
          <span className="badge badge-brand">{phase === "live" && !reconnecting ? <LiveDot className="h-1.5 w-1.5" /> : null}Live-Gast</span>
          <h1 className="pt-1 text-2xl font-semibold tracking-tight">{channel ? `Im Studio bei ${channel}` : "Einladung ins Studio"}</h1>
          {host && <p className="text-sm text-muted">Eingeladen von {host}</p>}
        </header>

        {message && <div className={phase === "ended" ? "well text-sm text-secondary" : "callout callout-danger"}>{message}</div>}

        {phase === "loading" && (
          <p className="flex items-center gap-2.5 text-sm text-muted">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-[color:var(--border-strong)] border-t-brand-primary" aria-hidden="true" />
            Einladung wird geprüft …
          </p>
        )}

        {(phase === "ready" || phase === "joining") && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void join();
            }}
          >
            <div className="callout callout-warning">
              <div><strong>Bitte Kopfhörer aufsetzen.</strong> Sonst hört das Studio sich selbst über dein Mikrofon.</div>
            </div>
            <label className="block" htmlFor="guest-name">
              <span className="field-label">Dein Name (so sieht dich das Studio)</span>
              <input
                id="guest-name"
                required
                maxLength={60}
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input !h-11 !text-base"
              />
            </label>
            <button type="submit" disabled={phase === "joining" || !name.trim()} className="btn btn-primary btn-lg w-full">
              {phase === "joining" ? "Verbinde …" : "Mikrofon erlauben und beitreten"}
            </button>
            <p className="text-xs text-muted">Der Link gilt einmal, bis {until} Uhr. Es wird nichts aufgezeichnet.</p>
          </form>
        )}

        {phase === "live" && (
          <section className="space-y-4" aria-live="polite">
            <div className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium ${reconnecting || !hostConnected ? "bg-amber-500/10 text-amber-800 dark:text-amber-200" : "bg-emerald-500/10 text-emerald-800 dark:text-emerald-200"}`}>
              <span className={`inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full ${reconnecting ? "bg-amber-400" : hostConnected ? "bg-emerald-500" : "bg-amber-400"}`} />
              {reconnecting ? "Verbindung weg – verbinde neu …" : hostConnected ? "Du bist mit dem Studio verbunden." : "Verbunden – das Studio ist gleich da."}
            </div>
            <Meter level={muted ? 0 : meter.mic} label="Dein Mikrofon" />
            <Meter level={meter.ret} label="Das Studio (Kopfhörer)" />
            <label className="block text-sm" htmlFor="guest-volume">
              <span className="field-label">Lautstärke im Kopfhörer</span>
              <input id="guest-volume" type="range" min={0} max={2} step={0.05} value={volume} onChange={(e) => changeVolume(Number(e.target.value))} className="mt-1 w-full" />
            </label>
            <button
              type="button"
              onClick={toggleMute}
              aria-pressed={muted}
              className={`btn w-full !h-14 text-base font-semibold ${muted ? "btn-danger" : "btn-secondary"}`}
            >
              {muted ? "Stumm – tippen zum Sprechen" : "Mikrofon an – tippen für Stumm"}
            </button>
            <p className="text-xs text-muted">
              Verzögerung ~{(rtt ?? 0) + meter.bufferMs + 20} ms · Aussetzer {(meter.loss * 100).toFixed(1).replace(".", ",")} %
            </p>
            <button type="button" onClick={leave} className="btn btn-ghost w-full">
              Verlassen
            </button>
          </section>
        )}
      </main>
    </AuthRahmen>
  );
}
